// React
import React from "react";

import { MarkEmailReadOutlined, MarkEmailUnreadOutlined } from "@mui/icons-material";

import { useTranslation } from "react-i18next";
// Third party imports
import { useDispatch } from "react-redux";

// Components
import { ActionMenuButton, mergeProps, openItem, useContextMenu } from "components/ui/ContextMenu";

// Redux
import { switchToReadLatest, switchToUnread } from "redux/slices/notificationsSlice";
import { setError } from "redux/slices/snackbarSlice";

// API
import { updateNotification } from "api/notifications";

// Helpers
import { formatTimeAgo } from "./helpers";

const NotificationItem = ({ notification, onClick }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const menu = useContextMenu();
  const { actor, verb, created_at, is_read, display_text } = notification;

  const handleMarkAsRead = async (e) => {
    e?.stopPropagation();
    try {
      await updateNotification(notification.id, { is_read: true });
      dispatch(switchToReadLatest(notification.id));
    } catch (error) {
      dispatch(setError(t("notifications:notificationItem.markAsReadError")));
    }
  };

  const handleMarkAsUnread = async (e) => {
    e?.stopPropagation();
    try {
      await updateNotification(notification.id, { is_read: false });
      dispatch(switchToUnread(notification.id));
    } catch (error) {
      dispatch(setError(t("notifications:notificationItem.markAsUnreadError")));
    }
  };

  const getMenuItems = () => [
    openItem(() => onClick?.()),
    is_read
      ? {
          id: "mark-unread",
          label: t("notifications:notificationItem.markAsUnread"),
          icon: MarkEmailUnreadOutlined,
          onClick: () => handleMarkAsUnread(),
        }
      : {
          id: "mark-read",
          label: t("notifications:notificationItem.markAsRead"),
          icon: MarkEmailReadOutlined,
          onClick: () => handleMarkAsRead(),
        },
  ];

  // Enter / Space open it, as on a button (the menu key and Shift+F10 come from the menu binding)
  const handleKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="surface-card surface-card--interactive group mr-1 p-4"
      onClick={onClick}
      {...mergeProps(menu.bind(getMenuItems), { onKeyDown: handleKeyDown })}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator */}
        {!is_read && (
          <div
            className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary"
            role="img"
            aria-label={t("notifications:notifications.tabUnread")}
          />
        )}

        {/* Avatar */}
        <div className="flex-shrink-0">
          {actor?.avatar?.file ? (
            <img
              src={actor.avatar.file}
              alt={actor.avatar.alt || t("notifications:notificationItem.avatarAlt")}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                {actor?.first_name?.[0]}
                {actor?.last_name?.[0]}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 mb-1">
                {actor?.first_name} {actor?.last_name}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                {display_text || `${verb} in ${notification.target_model_data?.model}`}
              </p>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <ActionMenuButton items={getMenuItems} className="hover-action" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500">{formatTimeAgo(created_at)}</span>
            <button
              type="button"
              onClick={is_read ? handleMarkAsUnread : handleMarkAsRead}
              className="cursor-pointer rounded-md px-2 py-1 text-sm font-medium text-primary-strong transition-colors hover:bg-primary-soft focus-visible:outline-2 focus-visible:outline-primary"
            >
              {is_read
                ? t("notifications:notificationItem.markAsUnread")
                : t("notifications:notificationItem.markAsRead")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
